import { initRequest } from '../api/request.js';

const restApi = initRequest(process.env.BACKEND_API);

export async function getCollectionsById({ id, eventPrivateApiKey, eventPublicApiKey, withProductData = true }) {
  return restApi.post('/ecommerce/collections/get-by-id', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    id,
    withProductData,
  });
}

export async function getCollectionsByEventName(eventName) {
  return restApi.post(
    '/occo-owner/ecommerce/collections/get-by-event-name',
    {
      eventName,
    },
    { addAuth: true }
  );
}

export async function getCollectionsWithProductsByEventName({ eventPrivateApiKey, eventPublicApiKey, eventName }) {
  return restApi.post('/ecommerce/collections/get-by-event-name', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    eventName,
  });
}

export async function createShoppingCard({
  lineItems = [],
  shippingAddress = null,
  eventPrivateApiKey,
  eventPublicApiKey,
}) {
  return restApi.post('/ecommerce/cart/create', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    line_items: lineItems,
    shipping_address: shippingAddress,
  });
}

export async function addLineItemsToCart({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/add-line-items', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    line_items: lineItems,
    cart_id: cartId,
  });
}

export async function createDraftOrder({ attendeeUid, cartId, eventPrivateApiKey }) {
  return restApi.post('/ecommerce/order/create-draft-order', {
    attendee_uid: attendeeUid,
    event_private_api_key: eventPrivateApiKey,
    cart_id: cartId,
  });
}

export async function completeDraftOrder({
  attendeeUid,
  draftOrderId,
  employeeSecretCode,
  authRequired,
  isByCash,
  eventPrivateApiKey,
}) {
  return restApi.post('/ecommerce/order/complete-draft-order', {
    attendee_uid: attendeeUid,
    event_private_api_key: eventPrivateApiKey,
    draft_order_id: draftOrderId,
    employee_secret_code: employeeSecretCode,
    auth_required: authRequired,
    is_by_cash: isByCash,
  });
}

export async function completeShopifyDraftOrderSelfCheckout({
  attendeeUid,
  draftOrderId,
  gateway,
  tag,
  eventPrivateApiKey,
}) {
  return restApi.post('/ecommerce/order/complete-draft-order-self-checkout', {
    attendee_uid: attendeeUid,
    event_private_api_key: eventPrivateApiKey,
    draft_order_id: draftOrderId,
    gateway,
    tag,
  });
}

export async function updateCartLineItems({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/update-line-items', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    line_items: lineItems,
    cart_id: cartId,
  });
}

export async function updateCartShippingAddress({ cartId, shippingAddress, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/update-address', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    cart_id: cartId,
    shipping_address: shippingAddress,
  });
}

export async function applyDiscountToCart({ cartId, discountCode, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/apply-discount', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    cart_id: cartId,
    discount_code: discountCode,
  });
}

export async function getShoppingCard({ cartId, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/get-by-id', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    cart_id: cartId,
  });
}

export async function getProductsByEventName({ withCombos = true, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/products/get-by-event-name',
    {
      eventName,
      withCombos,
    },
    { addAuth: true }
  );
}

export async function getShopLocationCodeList({ eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/products/shop-location-codes/list',
    {
      eventName,
    },
    { addAuth: true }
  );
}

export async function createProduct({ productData, eventName }) {
  const extendedProductData = {
    ...productData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/products/create', extendedProductData, { addAuth: true });
}

export async function deleteProduct({ productData, eventName }) {
  const extendedProductData = {
    ...productData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/products/delete', extendedProductData, { addAuth: true });
}

export async function editProduct({ productData, eventName }) {
  const extendedProductData = {
    ...productData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/products/edit', extendedProductData, { addAuth: true });
}

export async function createCollection({ collectionData, eventName }) {
  const extendedCollectionData = {
    ...collectionData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/collections/create', extendedCollectionData, { addAuth: true });
}

export async function editCollection({ collectionData, eventName }) {
  const extendedCollectionData = {
    ...collectionData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/collections/edit', extendedCollectionData, { addAuth: true });
}

export async function deleteCollection({ collectionData, eventName }) {
  const extendedCollectionData = {
    ...collectionData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/collections/delete', extendedCollectionData, { addAuth: true });
}

export async function importExternalOrders({ tag, startDate, endDate, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/order/import-external-orders',
    {
      eventName,
      tag,
      startDate,
      endDate,
    },
    { addAuth: true }
  );
}

export async function printOrderPhotos({ orderId, timeBasedKey, eventPrivateApiKey, eventName }) {
  return restApi.post(
    '/ecommerce/web-hook/print-order-photos',
    {
      event_private_api_key: eventPrivateApiKey,
      callUniqueKey: `${timeBasedKey}${orderId}`,
      eventName,
      orderId,
    },
    { addAuth: true }
  );
}

export async function getEventOrders({ startDate, endDate, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/order/get-orders-by-event-and-date',
    {
      eventName,
      startDate,
      endDate,
    },
    { addAuth: true }
  );
}
